/**
 * SessionExpiredOverlay
 * Appears over the current page when a 401 is received (token expired).
 * User re-authenticates inline — the page behind is preserved exactly as-is,
 * so no work is lost (unsaved form state, drafted invoice lines, etc.).
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ShieldAlert, Eye, EyeOff, LogIn } from 'lucide-react';

export default function SessionExpiredOverlay() {
  const { login } = useAuth();
  const rootRef = useRef(null);

  // Layer audit 2026-09-01: if a Radix modal dialog was open when the 401
  // landed, it (a) sets body pointer-events:none and (b) yanks focus back on
  // any focusin outside itself — making this login box un-clickable AND
  // un-typeable, with a hard reload (work lost) as the only exit. We must
  // win both locks WITHOUT closing the dialog: closing it would destroy the
  // very unsaved work this overlay promises to preserve. pointerEvents:auto
  // (inline, below) restores clicks; this capture-phase guard hides our
  // focus events from Radix's document-level focus-steal listener.
  useEffect(() => {
    const guard = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('focusin', guard, true);
    return () => document.removeEventListener('focusin', guard, true);
  }, []);
  const [email, setEmail] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('agripos_user') || '{}');
      return u.email || '';
    } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      // AuthContext will clear sessionExpired after successful login
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', pointerEvents: 'auto' }}
      data-testid="session-expired-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden"
        style={{ maxWidth: 400 }}
      >
        {/* Header */}
        <div className="bg-[#1A4D2E] px-6 pt-7 pb-5 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <ShieldAlert size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Session Expired
              </p>
              <p className="text-xs text-emerald-200 mt-0.5">
                Your session timed out. Sign in to continue where you left off.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="your@email.com"
              autoFocus
              data-testid="session-expired-email"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="••••••••"
                data-testid="session-expired-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="session-expired-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full bg-[#1A4D2E] hover:bg-[#14532d] disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="session-expired-submit"
          >
            {busy ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <LogIn size={15} />
            )}
            {busy ? 'Signing in…' : 'Continue where I left off'}
          </button>

          <p className="text-center text-xs text-slate-400 pb-1">
            Your current work is preserved. Just sign in to resume.
          </p>
        </form>
      </div>
    </div>
  );
}
